import { type ChangeEvent, type FormEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useAddOrganizationMember,
  useCreateOrganization,
  useDeactivateOrganization,
  useOrganizationMembers,
  useOrganizations,
  useRemoveOrganization,
  useRemoveOrganizationMember,
  useUpdateOrganization,
  useUploadOrganizationLogo,
} from '@/hooks/useOrganizations';
import { PageSpinner, Spinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { apiClient, getApiErrorMessage } from '@/api/client';
import { unwrapList } from '@/api/unwrap';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Table, type Column } from '@/components/ui/Table';
import { organizationRoleLabel } from '@/lib/roles';
import type { Organization, OrganizationRole } from '@/api/types';

// Organization membership only supports the OrganizationAdmin/Organizer staff
// roles (enforced in Domain.Organizations.OrganizationMember) - being a Judge
// or Competitor is always set per competition instead (assign them there),
// never as a blanket org-wide membership role.
const MEMBER_ROLE_OPTIONS: OrganizationRole[] = ['OrganizationAdmin', 'Organizer'];

interface FormState {
  name: string;
  description: string;
}

const emptyForm: FormState = { name: '', description: '' };

/**
 * Minimal shape we need for the member-picker dropdown. Fetched directly via
 * apiClient (bypassing api/users.ts and hooks/useUsers.ts, which another
 * agent is actively reworking) to avoid stepping on that work.
 */
interface UserOption {
  id: string;
  email: string;
}

function useUserOptions() {
  return useQuery({
    queryKey: ['organizations-admin', 'user-options'],
    queryFn: () =>
      apiClient
        .get('/users', { params: { page: 1, pageSize: 200 } })
        .then((r) => unwrapList<UserOption>(r.data).items),
  });
}

function MembersPanel({ org }: { org: Organization }) {
  const members = useOrganizationMembers(org.id);
  const userOptions = useUserOptions();
  const addMember = useAddOrganizationMember(org.id);
  const removeMember = useRemoveOrganizationMember(org.id);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<OrganizationRole>('Organizer');

  const usersById = new Map((userOptions.data ?? []).map((u) => [u.id, u]));

  async function handleAddMember(e: FormEvent) {
    e.preventDefault();
    if (!selectedUserId) return;
    await addMember.mutateAsync({ userId: selectedUserId, role: selectedRole });
    setSelectedUserId('');
  }

  return (
    <div className="flex flex-col gap-3">
      {members.isLoading && <Spinner />}
      {members.isError && (
        <ErrorState message={getApiErrorMessage(members.error)} onRetry={() => members.refetch()} />
      )}
      {members.isSuccess && members.data.length === 0 && <EmptyState title="Še ni članov" />}
      {members.isSuccess && members.data.length > 0 && (
        <ul className="flex flex-col gap-2">
          {members.data.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2 rounded-control border border-line px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-fg">{usersById.get(m.userId)?.email ?? m.userId}</span>
                <Badge>{organizationRoleLabel(m.role)}</Badge>
              </div>
              <Button
                variant="danger"
                size="sm"
                isLoading={removeMember.isPending}
                onClick={() => removeMember.mutate(m.userId)}
              >
                Odstrani
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleAddMember} className="flex flex-col gap-3 border-t border-line pt-3">
        {addMember.isError && <Alert variant="error">{getApiErrorMessage(addMember.error)}</Alert>}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Uporabnik"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            disabled={userOptions.isLoading}
          >
            <option value="">Izberite uporabnika…</option>
            {(userOptions.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
              </option>
            ))}
          </Select>
          <Select label="Vloga" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value as OrganizationRole)}>
            {MEMBER_ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {organizationRoleLabel(r)}
              </option>
            ))}
          </Select>
        </div>
        <Button type="submit" size="sm" isLoading={addMember.isPending} disabled={!selectedUserId} className="self-start">
          Dodaj člana
        </Button>
      </form>
    </div>
  );
}

export function AdminOrganizationsPage() {
  const organizations = useOrganizations({ page: 1, pageSize: 100 });
  const createOrganization = useCreateOrganization();
  const deactivateOrganization = useDeactivateOrganization();
  const removeOrganization = useRemoveOrganization();
  const [isCreating, setIsCreating] = useState(false);
  const [editing, setEditing] = useState<Organization | null>(null);
  const [managingMembers, setManagingMembers] = useState<Organization | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const updateOrganization = useUpdateOrganization(editing?.id ?? '');
  const uploadLogo = useUploadOrganizationLogo(editing?.id ?? '');

  function openCreate() {
    setForm(emptyForm);
    setIsCreating(true);
  }

  function openEdit(o: Organization) {
    setForm({ name: o.name, description: o.description ?? '' });
    setEditing(o);
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    await createOrganization.mutateAsync(form);
    setIsCreating(false);
  }

  async function handleUpdate(e: FormEvent) {
    e.preventDefault();
    await updateOrganization.mutateAsync(form);
    setEditing(null);
  }

  async function handleLogoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Refresh `editing` from the response so the preview updates immediately
    // instead of showing the stale org snapshot captured when the modal opened.
    const updated = await uploadLogo.mutateAsync(file);
    setEditing(updated);
    e.target.value = '';
  }

  const columns: Column<Organization>[] = [
    {
      header: 'Logotip',
      accessor: (o) =>
        o.logoUrl ? (
          <img src={o.logoUrl} alt={`${o.name} logo`} className="h-8 w-8 rounded-photo object-contain" />
        ) : (
          <span className="text-xs text-fg-muted">—</span>
        ),
    },
    { header: 'Naziv', accessor: (o) => o.name },
    { header: 'Opis', accessor: (o) => o.description ?? '—' },
    { header: 'Status', accessor: (o) => (o.isActive === false ? <Badge>Neaktivna</Badge> : <Badge>Aktivna</Badge>) },
    {
      header: 'Dejanja',
      accessor: (o) => (
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => openEdit(o)}>
            Uredi
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setManagingMembers(o)}>
            Člani
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={o.isActive === false}
            onClick={() => {
              if (confirm(`Deaktiviraj organizacijo "${o.name}"? Tega dejanja od tukaj ni mogoče razveljaviti.`)) {
                deactivateOrganization.mutate(o.id);
              }
            }}
          >
            Deaktiviraj
          </Button>
          <Button
            variant="danger"
            size="sm"
            // Backend requires the organization to already be deactivated
            // before it can be permanently deleted (see
            // OrganizationsService.DeleteAsync) - a deliberate two-step
            // confirmation so a live organization can't be erased in one click.
            disabled={o.isActive !== false}
            title={o.isActive !== false ? 'Najprej deaktivirajte organizacijo.' : undefined}
            isLoading={removeOrganization.isPending && removeOrganization.variables === o.id}
            onClick={() => {
              if (
                confirm(
                  `Trajno izbriši organizacijo "${o.name}"? To bo izbrisalo TUDI vsa njena tekmovanja in vse prispevke v njih. Tega dejanja ni mogoče razveljaviti.`,
                )
              ) {
                removeOrganization.mutate(o.id);
              }
            }}
          >
            Izbriši
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-fg">Organizacije</h1>
        <Button onClick={openCreate}>Nova organizacija</Button>
      </div>

      {(deactivateOrganization.isError || removeOrganization.isError) && (
        <Alert variant="error">{getApiErrorMessage(deactivateOrganization.error ?? removeOrganization.error)}</Alert>
      )}

      {organizations.isLoading && <PageSpinner />}
      {organizations.isError && (
        <ErrorState message={getApiErrorMessage(organizations.error)} onRetry={() => organizations.refetch()} />
      )}
      {organizations.isSuccess && organizations.data.items.length === 0 && (
        <EmptyState title="Še ni organizacij" />
      )}
      {organizations.isSuccess && organizations.data.items.length > 0 && (
        <Table columns={columns} rows={organizations.data.items} getRowKey={(o) => o.id} />
      )}

      {(isCreating || editing) && (
        <Modal
          title={editing ? `Uredi ${editing.name}` : 'Nova organizacija'}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
        >
          <form onSubmit={editing ? handleUpdate : handleCreate} className="flex flex-col gap-3">
            {(createOrganization.isError || updateOrganization.isError) && (
              <Alert variant="error">
                {getApiErrorMessage(createOrganization.error ?? updateOrganization.error)}
              </Alert>
            )}
            <Input
              label="Naziv"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Input
              label="Opis"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <Button
              type="submit"
              isLoading={createOrganization.isPending || updateOrganization.isPending}
              className="self-start"
            >
              {editing ? 'Shrani spremembe' : 'Ustvari organizacijo'}
            </Button>

            {editing && (
              <div className="flex flex-col gap-2 border-t border-line pt-3">
                {uploadLogo.isError && <Alert variant="error">{getApiErrorMessage(uploadLogo.error)}</Alert>}
                <div className="flex items-center gap-3">
                  {editing.logoUrl ? (
                    <img
                      src={editing.logoUrl}
                      alt={`${editing.name} logo`}
                      className="h-12 w-12 rounded-photo border border-line object-contain"
                    />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-photo border border-dashed border-line-strong text-xs text-fg-muted">
                      Brez logotipa
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-fg-secondary">
                      Logotip{' '}
                      <span className="font-normal text-fg-muted">
                        (prikazan, ko je izbrano eno od tekmovanj te organizacije)
                      </span>
                    </label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/svg+xml"
                      disabled={uploadLogo.isPending}
                      onChange={handleLogoChange}
                      className="text-sm text-fg-secondary file:mr-3 file:h-8 file:rounded-control file:border file:border-line-strong file:bg-surface-2 file:px-3 file:text-[13px] file:font-medium file:text-fg hover:file:bg-surface-3"
                    />
                  </div>
                </div>
              </div>
            )}
          </form>
        </Modal>
      )}

      {managingMembers && (
        <Modal title={`Člani organizacije ${managingMembers.name}`} onClose={() => setManagingMembers(null)}>
          <MembersPanel org={managingMembers} />
        </Modal>
      )}
    </div>
  );
}
